import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import errorStateImage from '@/assets/img/error-state.png';
import { analytics } from '@/features/analytics/service';
import { logger } from '@/features/logging/service/logger';
import { Sentry } from '@/integrations/sentry';
import { Button } from '@/shared/components/ui/button';

interface DefaultErrorComponentProps {
  error: Error;
}

export function DefaultErrorComponent({ error }: DefaultErrorComponentProps) {
  const navigate = useNavigate();

  useEffect(() => {
    // Send error to Sentry for monitoring
    Sentry.captureException(error, {
      tags: {
        component: 'DefaultErrorComponent',
        pathname: window.location.pathname,
      },
    });

    // Track error page view for product analytics
    try {
      analytics.captureEvent('Duos Error Page Viewed', {
        errorMessage: error.message,
        errorName: error.name,
        pathname: window.location.pathname,
      });
    } catch (analyticsError) {
      // Don't let analytics failure break the error page
      logger.error(
        '[Analytics] Failed to capture error event:',
        analyticsError,
      );
    }
  }, [error]);

  const handleTryAgain = () => {
    window.location.reload();
  };

  const handleGoBack = () => {
    void navigate({ to: '/' });
  };

  return (
    <div className="flex h-full w-full flex-col items-center justify-center px-5">
      <div className="flex w-full max-w-md flex-col items-center gap-5">
        <div className="relative size-50 shrink-0 overflow-hidden rounded-full">
          <img
            src={errorStateImage}
            alt="Error illustration"
            className="size-full object-cover"
          />
        </div>

        <div className="flex w-full flex-col items-center gap-2">
          <h1 className="text-center text-2xl leading-none font-extrabold text-white">
            Oops, that didn't work
          </h1>
          <p className="text-cold-400 text-center text-sm font-medium">
            Looks like something didn't load properly.
          </p>
        </div>

        <div className="flex w-full flex-col items-center gap-3">
          <Button onClick={handleTryAgain}>Let's try again</Button>
          <Button variant="ghost" onClick={handleGoBack}>
            Go back
          </Button>
        </div>
      </div>
    </div>
  );
}
